/** Tabbed operator editor — OPERATOR_OPS #4.
 *
 * Embedded in the operator detail page (AdminOperatorDetailPage). Tabs:
 * Details (identity, read-only), Schedule (weekly working hours + time-off
 * → PUT /operators/{id}/schedule), Campaigns (which campaigns the operator
 * is assigned to → PUT /operators/{id}/campaigns), Permissions (org roles
 * → PUT /operators/{id}/roles). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import PhoneInput from "@/components/PhoneInput";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { useHasPermission } from "@/lib/permissions";

type Tab = "details" | "schedule" | "campaigns" | "permissions";

type Slot = { weekday: number; start_time: string; end_time: string };
type TimeOff = { start_at: string; end_at: string; reason: string };

type ScheduleResp = {
  has_schedule: boolean;
  timezone: string;
  effective_timezone: string;
  slots: Slot[];
  time_off: (TimeOff & { id: string })[];
};

type CampaignsResp = {
  assigned_ids: string[];
  campaigns: { id: string; name: string }[];
};

type RolesResp = {
  assigned_slugs: string[];
  roles: {
    slug: string;
    name: string;
    description: string;
    is_system: boolean;
  }[];
};

export type OperatorSummary = {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  phone?: string;
  campaign_count: number;
};

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Normalise free-typed input to a 24h "HH:MM" string. Accepts "9", "9:5",
// "930", "9h30", etc. Clamps to a valid time; empty → "00:00".
function normHHMM(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (!digits) return "00:00";
  const pad = (n: number) => String(n).padStart(2, "0");
  if (digits.length <= 2) {
    return `${pad(Math.min(23, Number(digits)))}:00`;
  }
  const h = Math.min(23, Number(digits.slice(0, digits.length - 2)));
  const m = Math.min(59, Number(digits.slice(-2)));
  return `${pad(h)}:${pad(m)}`;
}

/** A guaranteed 24-hour time field. A native <input type="time"> renders in
 * the browser's locale (12h AM/PM in en-US, and its `lang` attr is ignored for
 * the picker), so we use a plain text field bound to the 24h value instead:
 * free typing while focused, normalised to "HH:MM" on blur. */
function TimeField({
  value,
  onCommit,
}: {
  value: string;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      type="text"
      inputMode="numeric"
      maxLength={5}
      placeholder="HH:MM"
      className="input w-full text-xs tabular-nums"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(normHHMM(draft))}
    />
  );
}

export function OperatorEditorTabs({
  operator,
}: {
  operator: OperatorSummary;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("details");
  const uid = operator.user_id;
  const navigate = useNavigate();
  const canEditProfile = useHasPermission("org.member.update");
  const canRemove = useHasPermission("org.member.remove");

  // Deactivate this person's membership in the current org (reversible).
  const removeMember = useMutation({
    mutationFn: () => api.delete(ApiPaths.orgMember(uid)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-members"] });
      qc.invalidateQueries({ queryKey: ["operators"] });
      navigate("/admin/members");
    },
  });

  // ---- Details (name on the global User; job_title + phone per-org) ----
  const [profile, setProfile] = useState({
    first_name: operator.first_name ?? "",
    last_name: operator.last_name ?? "",
    job_title: operator.job_title ?? "",
    phone: operator.phone ?? "",
  });
  const saveProfile = useMutation({
    mutationFn: async () =>
      (await api.patch(ApiPaths.operatorProfile(uid), profile)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operators"] });
      void qc.invalidateQueries({ queryKey: ["org-members"] });
    },
  });

  // ---- Schedule ----
  const [timezone, setTimezone] = useState("");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [timeOff, setTimeOff] = useState<TimeOff[]>([]);
  const scheduleQ = useQuery({
    queryKey: ["operator-schedule", uid],
    queryFn: async () =>
      (await api.get<ScheduleResp>(ApiPaths.operatorSchedule(uid))).data,
  });
  useEffect(() => {
    if (!scheduleQ.data) return;
    setTimezone(scheduleQ.data.timezone || "");
    setSlots(scheduleQ.data.slots);
    setTimeOff(
      scheduleQ.data.time_off.map((to) => ({
        start_at: to.start_at,
        end_at: to.end_at,
        reason: to.reason,
      })),
    );
  }, [scheduleQ.data]);

  const saveSchedule = useMutation({
    mutationFn: async () =>
      (
        await api.put(ApiPaths.operatorSchedule(uid), {
          timezone,
          slots,
          time_off: timeOff,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operators"] });
      void qc.invalidateQueries({ queryKey: ["operator-schedule", uid] });
    },
  });

  // ---- Campaigns ----
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const campaignsQ = useQuery({
    queryKey: ["operator-campaigns", uid],
    queryFn: async () =>
      (await api.get<CampaignsResp>(ApiPaths.operatorCampaigns(uid))).data,
  });
  useEffect(() => {
    if (campaignsQ.data)
      setAssigned(new Set(campaignsQ.data.assigned_ids));
  }, [campaignsQ.data]);

  const saveCampaigns = useMutation({
    mutationFn: async () =>
      (
        await api.put(ApiPaths.operatorCampaigns(uid), {
          campaign_ids: Array.from(assigned),
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operators"] });
      void qc.invalidateQueries({ queryKey: ["operator-campaigns", uid] });
    },
  });

  // ---- Permissions (org roles) ----
  const [roleSlugs, setRoleSlugs] = useState<Set<string>>(new Set());
  const rolesQ = useQuery({
    queryKey: ["operator-roles", uid],
    queryFn: async () =>
      (await api.get<RolesResp>(ApiPaths.operatorRoles(uid))).data,
  });
  useEffect(() => {
    if (rolesQ.data) setRoleSlugs(new Set(rolesQ.data.assigned_slugs));
  }, [rolesQ.data]);
  const saveRoles = useMutation({
    mutationFn: async () =>
      (
        await api.put(ApiPaths.operatorRoles(uid), {
          role_slugs: Array.from(roleSlugs),
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator-roles", uid] });
      void qc.invalidateQueries({ queryKey: ["org-members"] });
    },
  });

  // ── Per-day schedule editing ─────────────────────────────────────────
  // A weekday holds up to two working windows; the operator sees them as
  // "first shift / break / second shift" where the break is exactly the gap
  // between the two shifts (its edges are bound to the shift boundaries).
  const hhmm = (t: string) => t.slice(0, 5);

  // The (≤2) windows for a weekday, earliest first.
  const dayWins = (wd: number): Slot[] =>
    slots
      .filter((s) => s.weekday === wd)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .slice(0, 2);

  // Replace all windows for a weekday.
  const setDay = (
    wd: number,
    wins: { start_time: string; end_time: string }[],
  ) =>
    setSlots((all) => [
      ...all.filter((s) => s.weekday !== wd),
      ...wins.map((w) => ({ weekday: wd, ...w })),
    ]);

  const setWorking = (wd: number, on: boolean) =>
    setDay(wd, on ? [{ start_time: "09:00", end_time: "17:00" }] : []);

  const setSplit = (wd: number, on: boolean) => {
    const w = dayWins(wd);
    const first = w[0] ?? { start_time: "09:00", end_time: "17:00" };
    if (on) {
      // Insert a ~1h break around the shift's midpoint, so the two halves
      // stay valid regardless of when the shift is (morning or afternoon).
      const toMin = (t: string) => {
        const [h = 0, m = 0] = hhmm(t).split(":").map(Number);
        return h * 60 + m;
      };
      const pad = (n: number) => String(n).padStart(2, "0");
      const toStr = (x: number) => `${pad(Math.floor(x / 60) % 24)}:${pad(x % 60)}`;
      const a = toMin(first.start_time);
      const b = toMin(first.end_time) > a ? toMin(first.end_time) : a + 8 * 60;
      const brk = Math.min(60, Math.max(15, Math.floor((b - a) / 4)));
      let mid = a + Math.floor((b - a - brk) / 2);
      if (mid <= a) mid = a + Math.max(15, Math.floor((b - a) / 2));
      let s2 = mid + brk;
      if (s2 >= b) s2 = Math.max(mid + 15, b - 15);
      setDay(wd, [
        { start_time: toStr(a), end_time: toStr(mid) },
        { start_time: toStr(s2), end_time: toStr(b) },
      ]);
    } else {
      // Merge back into one continuous shift (first start → last end).
      const end = w[1] ? hhmm(w[1].end_time) : hhmm(first.end_time);
      setDay(wd, [{ start_time: hhmm(first.start_time), end_time: end }]);
    }
  };

  // Patch the first / second window of a day (times are the underlying
  // editable values; the break edges reuse first.end + second.start).
  const patchWin = (wd: number, which: 0 | 1, patch: Partial<Slot>) => {
    const w = dayWins(wd);
    const wins = w.map((s) => ({
      start_time: hhmm(s.start_time),
      end_time: hhmm(s.end_time),
    }));
    if (wins[which]) wins[which] = { ...wins[which], ...patch };
    setDay(wd, wins);
  };

  const TABS: Tab[] = ["details", "schedule", "campaigns", "permissions"];

  return (
    <div className="card">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-nb-border">
          {TABS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
                tab === k
                  ? "border-nb-mint font-medium text-nb-text"
                  : "border-transparent text-nb-text-3 hover:text-nb-text-2"
              }`}
            >
              {t(`admin.operators.tab_${k}`, k)}
            </button>
          ))}
        </div>

        <div className="mt-4 flex-1 overflow-y-auto">
          {tab === "details" && (
            <div className="space-y-3 text-sm">
              {/* Email is the login identity — read-only here. */}
              <div>
                <label className="label text-[11px]">
                  {t("auth.signup.email")}
                </label>
                <input className="input" value={operator.email} readOnly disabled />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label text-[11px]">
                    {t("admin.operators.firstName", "First name")}
                  </label>
                  <input
                    className="input"
                    value={profile.first_name}
                    disabled={!canEditProfile}
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, first_name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="label text-[11px]">
                    {t("admin.operators.lastName", "Last name")}
                  </label>
                  <input
                    className="input"
                    value={profile.last_name}
                    disabled={!canEditProfile}
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, last_name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="label text-[11px]">
                    {t("admin.operators.jobTitle", "Job title")}
                  </label>
                  <input
                    className="input"
                    value={profile.job_title}
                    disabled={!canEditProfile}
                    onChange={(e) =>
                      setProfile((p) => ({ ...p, job_title: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="label text-[11px]">
                    {t("admin.operators.phone", "Phone")}
                  </label>
                  <PhoneInput
                    value={profile.phone}
                    disabled={!canEditProfile}
                    onChange={(v) =>
                      setProfile((p) => ({ ...p, phone: v }))
                    }
                  />
                </div>
              </div>
              <p className="text-[10px] text-nb-text-3">
                {t(
                  "admin.operators.detailsHint",
                  "Name updates the person's account (all their orgs). Job title + phone are specific to this organization.",
                )}
              </p>
              {saveProfile.isError && (
                <p className="text-xs text-cvp-danger">
                  {String(saveProfile.error)}
                </p>
              )}
              {canEditProfile && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={saveProfile.isPending}
                  onClick={() => saveProfile.mutate()}
                >
                  {saveProfile.isPending
                    ? t("common.saving", "Saving…")
                    : t("admin.operators.saveDetails", "Save details")}
                </button>
              </div>
              )}

              {canRemove && (
                <div className="mt-4 border-t border-nb-border pt-3">
                  <p className="text-[10px] uppercase tracking-wide text-cvp-danger">
                    {t("admin.operators.dangerZone", "Danger zone")}
                  </p>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <span className="text-xs text-nb-text-2">
                      {t(
                        "admin.operators.removeHint",
                        "Deactivate this person's membership in this organization. Their account and other organizations are unaffected; you can re-invite them later.",
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn-danger shrink-0 text-sm"
                      disabled={removeMember.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            t("admin.operators.removeConfirm", {
                              email: operator.email,
                            }),
                          )
                        ) {
                          removeMember.mutate();
                        }
                      }}
                    >
                      {removeMember.isPending
                        ? t("admin.operators.removing", "Removing…")
                        : t(
                            "admin.operators.removeFromOrg",
                            "Remove from organization",
                          )}
                    </button>
                  </div>
                  {removeMember.isError && (
                    <p className="mt-1 text-xs text-cvp-danger">
                      {describeApiError(
                        removeMember.error,
                        t("admin.operators.removeFailed", "Could not remove member."),
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "schedule" && (
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs text-nb-text-3">
                  {t("admin.operators.timezone", "Timezone")}
                </span>
                <input
                  className="input mt-0.5"
                  placeholder="Europe/Paris"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                />
              </label>
              {WEEKDAYS.map((wd) => {
                const dayKey = DAY_KEYS[wd] ?? String(wd);
                const wins = dayWins(wd);
                const working = wins.length > 0;
                const hasBreak = wins.length >= 2;
                const first = wins[0];
                const second = wins[1];
                const rowCls = "mt-1 grid grid-cols-[6rem_1fr_1fr] items-center gap-2";
                return (
                <div
                  key={wd}
                  className="rounded border border-nb-border/60 p-2"
                >
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={working}
                      onChange={(e) => setWorking(wd, e.target.checked)}
                    />
                    <span className="text-xs font-medium">
                      {t(`admin.operators.day_${dayKey}`, dayKey)}
                    </span>
                    {!working && (
                      <span className="text-[10px] text-nb-text-3">
                        {t("admin.operators.dayOff", "Day off")}
                      </span>
                    )}
                  </label>

                  {working && first && (
                    <div className="mt-2 space-y-1">
                      {/* First shift */}
                      <div className={rowCls}>
                        <span className="text-[11px] text-nb-text-2">
                          {t("admin.operators.firstShift", "First shift")}
                        </span>
                        <TimeField
                          value={hhmm(first.start_time)}
                          onCommit={(v) => patchWin(wd, 0, { start_time: v })}
                        />
                        <TimeField
                          value={hhmm(first.end_time)}
                          onCommit={(v) => patchWin(wd, 0, { end_time: v })}
                        />
                      </div>

                      {/* Split-with-a-break toggle */}
                      <label className="flex items-center gap-2 pl-0.5">
                        <input
                          type="checkbox"
                          checked={hasBreak}
                          onChange={(e) => setSplit(wd, e.target.checked)}
                        />
                        <span className="text-[11px] text-nb-text-3">
                          {t("admin.operators.splitShift", "Split with a break")}
                        </span>
                      </label>

                      {hasBreak && second && (
                        <>
                          {/* Break = the gap; its edges are bound to the
                              shift boundaries (first.end … second.start). */}
                          <div className={rowCls}>
                            <span className="text-[11px] text-nb-text-2">
                              {t("admin.operators.breakWindow", "Break")}
                            </span>
                            <TimeField
                              value={hhmm(first.end_time)}
                              onCommit={(v) => patchWin(wd, 0, { end_time: v })}
                            />
                            <TimeField
                              value={hhmm(second.start_time)}
                              onCommit={(v) => patchWin(wd, 1, { start_time: v })}
                            />
                          </div>

                          {/* Second shift */}
                          <div className={rowCls}>
                            <span className="text-[11px] text-nb-text-2">
                              {t("admin.operators.secondShift", "Second shift")}
                            </span>
                            <TimeField
                              value={hhmm(second.start_time)}
                              onCommit={(v) => patchWin(wd, 1, { start_time: v })}
                            />
                            <TimeField
                              value={hhmm(second.end_time)}
                              onCommit={(v) => patchWin(wd, 1, { end_time: v })}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
              {saveSchedule.isError && (
                <p className="text-xs text-cvp-danger">
                  {String(saveSchedule.error)}
                </p>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={saveSchedule.isPending}
                  onClick={() => saveSchedule.mutate()}
                >
                  {saveSchedule.isPending
                    ? t("common.saving", "Saving…")
                    : t("admin.operators.saveSchedule", "Save schedule")}
                </button>
              </div>
            </div>
          )}

          {tab === "campaigns" && (
            <div className="space-y-2 text-sm">
              {(campaignsQ.data?.campaigns ?? []).length === 0 && (
                <p className="text-nb-text-3">
                  {t("admin.operators.noCampaigns", "No campaigns yet.")}
                </p>
              )}
              {(campaignsQ.data?.campaigns ?? []).map((c) => (
                <label key={c.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={assigned.has(c.id)}
                    onChange={(e) =>
                      setAssigned((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        return next;
                      })
                    }
                  />
                  <span>{c.name}</span>
                </label>
              ))}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={saveCampaigns.isPending}
                  onClick={() => saveCampaigns.mutate()}
                >
                  {saveCampaigns.isPending
                    ? t("common.saving", "Saving…")
                    : t("admin.operators.saveCampaigns", "Save campaigns")}
                </button>
              </div>
            </div>
          )}

          {tab === "permissions" && (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-nb-text-3">
                {t(
                  "admin.operators.permissionsLead",
                  "The org roles this operator holds. Roles grant permissions.",
                )}
              </p>
              {(rolesQ.data?.roles ?? []).map((r) => (
                <label key={r.slug} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={roleSlugs.has(r.slug)}
                    onChange={(e) =>
                      setRoleSlugs((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(r.slug);
                        else next.delete(r.slug);
                        return next;
                      })
                    }
                  />
                  <span>
                    <span className="font-medium">{r.name}</span>
                    {r.description && (
                      <span className="block text-[11px] text-nb-text-3">
                        {t(`roleDesc.${r.slug}`, { defaultValue: r.description })}
                      </span>
                    )}
                  </span>
                </label>
              ))}
              {saveRoles.isError && (
                <p className="text-xs text-cvp-danger">
                  {String(saveRoles.error)}
                </p>
              )}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={saveRoles.isPending}
                  onClick={() => saveRoles.mutate()}
                >
                  {saveRoles.isPending
                    ? t("common.saving", "Saving…")
                    : t("admin.operators.saveRoles", "Save roles")}
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
}
